import { useState, useEffect, useCallback, useRef } from "react";
import { companiesAPI, usersAPI } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { 
  Plus, 
  Edit, 
  Trash2, 
  Building2, 
  Users, 
  UserPlus, 
  UserMinus,
  FolderKanban,
  Euro,
  Shield,
  Eye,
  Check,
  Download,
  Upload,
  Database,
  FileJson,
  AlertTriangle
} from "lucide-react";

function formatCurrency(amount) {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR"
  }).format(amount || 0);
}

function CompanyCard({ company, onView, onAssign, onEdit, onDelete, onExport, onImport }) {
  const adminCount = company.admin_count || 0;
  const userCount = company.user_count || 0;
  const customerCount = company.customer_count || 0;
  const projectCount = company.project_count || 0;
  
  return (
    <Card className="card-hover">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Building2 className="h-6 w-6 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">{company.name}</CardTitle>
              {company.address && (
                <CardDescription className="text-xs line-clamp-1">
                  {company.address}
                </CardDescription>
              )}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 bg-muted rounded-lg">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
              <Shield className="h-3 w-3" />
              Admins
            </div>
            <p className="text-xl font-bold font-['Manrope']">{adminCount}</p>
          </div>
          <div className="p-3 bg-muted rounded-lg">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
              <Users className="h-3 w-3" />
              Benutzer
            </div>
            <p className="text-xl font-bold font-['Manrope']">{userCount}</p>
          </div>
          <div className="p-3 bg-muted rounded-lg">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
              <Users className="h-3 w-3" />
              Kunden
            </div>
            <p className="text-xl font-bold font-['Manrope']">{customerCount}</p>
          </div>
          <div className="p-3 bg-muted rounded-lg">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
              <FolderKanban className="h-3 w-3" />
              Projekte
            </div>
            <p className="text-xl font-bold font-['Manrope']">{projectCount}</p>
          </div>
        </div>

        <Separator />

        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="flex-1" onClick={onView}>
            <Eye className="h-4 w-4 mr-1" />
            Details
          </Button>
          <Button variant="outline" size="sm" onClick={onExport} title="Firmendaten exportieren">
            <Download className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={onImport} title="Firmendaten importieren">
            <Upload className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={onAssign}>
            <UserPlus className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={onEdit}>
            <Edit className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={onDelete} className="text-destructive hover:text-destructive">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function CompanyDetailDialog({ company, open, onOpenChange, onRemoveUser, onAssign, onCreateUser }) {
  if (!company) return null;
  
  const stats = company.stats || {};
  const users = company.users || [];
  const admins = company.admins || [];
  const usersLen = users.length;
  const adminsLen = admins.length;
  const customerCount = stats.customer_count || 0;
  const projectCount = stats.project_count || 0;
  const offerCount = stats.offer_count || 0;
  const totalRevenue = stats.total_revenue || 0;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Building2 className="h-6 w-6 text-primary" />
            </div>
            <div>
              <DialogTitle className="font-['Manrope']">{company.name}</DialogTitle>
              <DialogDescription>{company.address || "Keine Adresse"}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="grid grid-cols-4 gap-4 my-4">
          <div className="p-4 bg-muted rounded-lg text-center">
            <Users className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
            <p className="text-2xl font-bold font-['Manrope']">{customerCount}</p>
            <p className="text-xs text-muted-foreground">Kunden</p>
          </div>
          <div className="p-4 bg-muted rounded-lg text-center">
            <FolderKanban className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
            <p className="text-2xl font-bold font-['Manrope']">{projectCount}</p>
            <p className="text-xs text-muted-foreground">Projekte</p>
          </div>
          <div className="p-4 bg-muted rounded-lg text-center">
            <p className="text-2xl font-bold font-['Manrope']">{offerCount}</p>
            <p className="text-xs text-muted-foreground">Angebote</p>
          </div>
          <div className="p-4 bg-accent/10 rounded-lg text-center">
            <Euro className="h-5 w-5 mx-auto mb-1 text-accent" />
            <p className="text-xl font-bold font-['Manrope'] text-accent">
              {formatCurrency(totalRevenue)}
            </p>
            <p className="text-xs text-muted-foreground">Umsatz</p>
          </div>
        </div>

        <Tabs defaultValue="users">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="users">Benutzer ({usersLen})</TabsTrigger>
            <TabsTrigger value="admins">Admins ({adminsLen})</TabsTrigger>
          </TabsList>

          <TabsContent value="users" className="mt-4">
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-semibold">Alle Benutzer dieser Firma</h4>
              <Button size="sm" onClick={onCreateUser}>
                <UserPlus className="h-4 w-4 mr-1" />
                Neuen Benutzer erstellen
              </Button>
            </div>

            {usersLen > 0 ? (
              <ScrollArea className="h-64">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>E-Mail</TableHead>
                      <TableHead>Rolle</TableHead>
                      <TableHead className="text-right">Aktion</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">
                          {user.first_name} {user.last_name}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {user.email}
                        </TableCell>
                        <TableCell>
                          <Badge variant={user.role === "admin" ? "default" : "secondary"}>
                            {user.role === "admin" ? "Admin" : "Vertriebler"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onRemoveUser(user.id)}
                            className="text-destructive hover:text-destructive"
                          >
                            <UserMinus className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>Keine Benutzer zugewiesen</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="admins" className="mt-4">
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-semibold">Administratoren dieser Firma</h4>
              <Button size="sm" variant="outline" onClick={onAssign}>
                <UserPlus className="h-4 w-4 mr-1" />
                Admin zuweisen
              </Button>
            </div>

            {adminsLen > 0 ? (
              <div className="space-y-3">
                {admins.map((admin) => (
                  <Card key={admin.id} className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <Shield className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">{admin.first_name} {admin.last_name}</p>
                          <p className="text-sm text-muted-foreground">{admin.email}</p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onRemoveUser(admin.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <UserMinus className="h-4 w-4" />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Shield className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>Kein Admin zugewiesen</p>
                <Button variant="link" className="mt-2" onClick={onAssign}>
                  Admin zuweisen
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

export default function CompaniesPage() {
  const { user: currentUser } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState("");
  
  // Import states
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importType, setImportType] = useState(null); // 'full' or 'company'
  const [importCompany, setImportCompany] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importValidation, setImportValidation] = useState(null);
  const [importConfirmOpen, setImportConfirmOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  const fullDbFileInputRef = useRef(null);
  
  const [companyFormData, setCompanyFormData] = useState({
    name: "",
    address: "",
    phone: "",
    email: "",
    tax_id: "",
    tax_number: "",
    offer_number_start: 1,
    invoice_number_start: 1,
    credit_note_number_start: 1
  });
  
  const [userFormData, setUserFormData] = useState({
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    role: "admin"
  });

  const loadData = useCallback(async () => {
    try {
      const [companiesRes, usersRes] = await Promise.all([
        companiesAPI.list(),
        usersAPI.list()
      ]);
      setCompanies(companiesRes.data);
      setAllUsers(usersRes.data);
    } catch (error) {
      console.error("Failed to load data:", error);
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const resetCompanyForm = useCallback(() => {
    setEditingCompany(null);
    setCompanyFormData({ 
      name: "", 
      address: "", 
      phone: "", 
      email: "", 
      tax_id: "", 
      tax_number: "",
      offer_number_start: 1,
      invoice_number_start: 1,
      credit_note_number_start: 1
    });
  }, []);

  const resetUserForm = useCallback(() => {
    setUserFormData({ email: "", password: "", first_name: "", last_name: "", role: "admin" });
  }, []);

  const handleCompanySubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCompany) {
        await companiesAPI.update(editingCompany.id, companyFormData);
        toast.success("Firma aktualisiert");
      } else {
        await companiesAPI.create(companyFormData);
        toast.success("Firma erstellt");
      }
      setDialogOpen(false);
      resetCompanyForm();
      loadData();
    } catch (error) {
      console.error("Failed to save company:", error);
      toast.error("Fehler beim Speichern");
    }
  };

  const handleDeleteCompany = async (companyId) => {
    if (!window.confirm("Möchten Sie diese Firma wirklich löschen?")) return;
    try {
      await companiesAPI.delete(companyId);
      toast.success("Firma gelöscht");
      loadData();
    } catch (error) {
      console.error("Failed to delete company:", error);
      toast.error("Fehler beim Löschen");
    }
  };

  const handleViewCompany = async (companyId) => {
    try {
      const response = await companiesAPI.get(companyId);
      setSelectedCompany(response.data);
      setDetailDialogOpen(true);
    } catch (error) {
      console.error("Failed to load company details:", error);
      toast.error("Fehler beim Laden der Firmendetails");
    }
  };

  const handleEditCompany = (company) => {
    setEditingCompany(company);
    setCompanyFormData({
      name: company.name,
      address: company.address || "",
      phone: company.phone || "",
      email: company.email || "",
      tax_id: company.tax_id || "",
      tax_number: company.tax_number || "",
      offer_number_start: company.offer_number_start || 1,
      invoice_number_start: company.invoice_number_start || 1,
      credit_note_number_start: company.credit_note_number_start || 1
    });
    setDialogOpen(true);
  };

  const handleOpenAssignDialog = (company) => {
    setSelectedCompany(company);
    setSelectedUserId("");
    setAssignDialogOpen(true);
  };

  const handleAssignUser = async () => {
    if (!selectedUserId || !selectedCompany) return;
    try {
      await companiesAPI.assignAdmin(selectedCompany.id, selectedUserId);
      toast.success("Benutzer wurde der Firma zugewiesen");
      setAssignDialogOpen(false);
      loadData();
      if (detailDialogOpen && selectedCompany) {
        const response = await companiesAPI.get(selectedCompany.id);
        setSelectedCompany(response.data);
      }
    } catch (error) {
      console.error("Failed to assign user:", error);
      toast.error("Fehler beim Zuweisen");
    }
  };

  const handleRemoveUserFromCompany = async (userId) => {
    if (!selectedCompany) return;
    if (!window.confirm("Möchten Sie diesen Benutzer wirklich aus der Firma entfernen?")) return;
    try {
      await companiesAPI.removeUser(selectedCompany.id, userId);
      toast.success("Benutzer aus Firma entfernt");
      const response = await companiesAPI.get(selectedCompany.id);
      setSelectedCompany(response.data);
      loadData();
    } catch (error) {
      console.error("Failed to remove user:", error);
      toast.error("Fehler beim Entfernen");
    }
  };

  const handleCreateUserForCompany = async (e) => {
    e.preventDefault();
    if (!selectedCompany) return;
    try {
      await usersAPI.create({
        email: userFormData.email,
        password: userFormData.password,
        first_name: userFormData.first_name,
        last_name: userFormData.last_name,
        role: userFormData.role,
        company_ids: [selectedCompany.id]
      });
      toast.success("Benutzer erstellt und der Firma zugewiesen");
      setCreateUserDialogOpen(false);
      resetUserForm();
      loadData();
      const response = await companiesAPI.get(selectedCompany.id);
      setSelectedCompany(response.data);
    } catch (error) {
      console.error("Failed to create user:", error);
      toast.error("Fehler beim Erstellen");
    }
  };

  const getAvailableUsers = () => {
    if (!selectedCompany) return [];
    const companyId = selectedCompany.id;
    return allUsers.filter(u => {
      if (u.role === "superadmin") return false;
      if (!u.company_ids) return true;
      return !u.company_ids.includes(companyId);
    });
  };

  // Export functions
  const handleExportFullDatabase = async () => {
    try {
      toast.info("Export wird erstellt...");
      const response = await companiesAPI.exportFullDatabase();
      
      // Create download link
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `solarcrm_full_backup_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success("Datenbank-Export erfolgreich heruntergeladen");
    } catch (error) {
      console.error("Failed to export database:", error);
      toast.error("Fehler beim Exportieren der Datenbank");
    }
  };

  const handleExportCompany = async (companyId, companyName) => {
    try {
      toast.info("Export wird erstellt...");
      const response = await companiesAPI.exportCompany(companyId);
      
      // Create download link
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = companyName.replace(/[^a-zA-Z0-9]/g, '_');
      a.download = `${safeName}_backup_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Export für "${companyName}" erfolgreich heruntergeladen`);
    } catch (error) {
      console.error("Failed to export company:", error);
      toast.error("Fehler beim Exportieren der Firmendaten");
    }
  };

  // Import functions
  const handleOpenImportFullDatabase = () => {
    setImportType('full');
    setImportCompany(null);
    setImportFile(null);
    setImportValidation(null);
    fullDbFileInputRef.current?.click();
  };

  const handleOpenImportCompany = (company) => {
    setImportType('company');
    setImportCompany(company);
    setImportFile(null);
    setImportValidation(null);
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setImportFile(file);
    
    // Validate the file
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await companiesAPI.validateImport(formData);
      setImportValidation(response.data);
      
      // Check validation
      if (!response.data.valid) {
        toast.error(`Ungültige Backup-Datei: ${response.data.error}`);
        return;
      }
      
      // For company import, check if it matches
      if (importType === 'company' && importCompany) {
        if (response.data.export_type !== 'company_export') {
          toast.error("Dies ist kein Firmen-Backup. Bitte wählen Sie ein Firmen-spezifisches Backup.");
          return;
        }
        if (response.data.company_id !== importCompany.id) {
          toast.error(
            `Backup-Fehler: Dieses Backup gehört zu "${response.data.company_name}", nicht zu "${importCompany.name}".`
          );
          return;
        }
      }
      
      // For full database import
      if (importType === 'full') {
        if (response.data.export_type !== 'full_database') {
          toast.error("Dies ist kein vollständiges Datenbank-Backup.");
          return;
        }
      }
      
      // Open confirmation dialog
      setImportDialogOpen(true);
      
    } catch (error) {
      console.error("Validation error:", error);
      toast.error("Fehler beim Validieren der Backup-Datei");
    }
    
    // Reset the input
    event.target.value = '';
  };

  const handleImportConfirm = async () => {
    if (!importFile) return;
    
    setImporting(true);
    
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      
      let response;
      if (importType === 'full') {
        response = await companiesAPI.importFullDatabase(formData);
      } else if (importType === 'company' && importCompany) {
        response = await companiesAPI.importCompany(importCompany.id, formData);
      }
      
      if (response?.data?.success) {
        toast.success(response.data.message);
        
        // Show imported counts
        const counts = response.data.imported_counts || {};
        const summary = Object.entries(counts)
          .filter(([_, count]) => count > 0)
          .map(([name, count]) => `${name}: ${count}`)
          .join(', ');
        if (summary) {
          toast.info(`Importiert: ${summary}`);
        }
        
        // Refresh data
        fetchData();
      } else {
        toast.error(response?.data?.message || "Import fehlgeschlagen");
        
        if (response?.data?.errors?.length > 0) {
          response.data.errors.forEach(err => toast.error(err));
        }
      }
    } catch (error) {
      console.error("Import error:", error);
      const errorMsg = error.response?.data?.detail || "Fehler beim Importieren";
      toast.error(errorMsg);
    } finally {
      setImporting(false);
      setImportDialogOpen(false);
      setImportFile(null);
      setImportValidation(null);
    }
  };

  const userRole = currentUser ? currentUser.role : "";

  if (userRole !== "superadmin") {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="p-8 text-center">
          <Shield className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-xl font-semibold mb-2">Zugriff verweigert</h2>
          <p className="text-muted-foreground">
            Diese Seite ist nur für Superadmins zugänglich.
          </p>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const availableUsersForAssign = getAvailableUsers();
  const companyName = selectedCompany ? selectedCompany.name : "";

  return (
    <div className="space-y-6 animate-slide-in" data-testid="companies-page">
      {/* Hidden file inputs */}
      <input
        type="file"
        ref={fileInputRef}
        accept=".json"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />
      <input
        type="file"
        ref={fullDbFileInputRef}
        accept=".json"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />
      
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope']">Firmenverwaltung</h1>
          <p className="text-muted-foreground">
            Verwalten Sie alle Firmen und deren Benutzer
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handleExportFullDatabase}
            data-testid="export-full-db-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            DB exportieren
          </Button>
          <Button 
            variant="outline" 
            onClick={handleOpenImportFullDatabase}
            data-testid="import-full-db-btn"
          >
            <Upload className="h-4 w-4 mr-2" />
            DB importieren
          </Button>
          <Button onClick={() => { resetCompanyForm(); setDialogOpen(true); }} data-testid="new-company-btn">
            <Plus className="h-4 w-4 mr-2" />
            Neue Firma
          </Button>
        </div>
      </div>

      {companies.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {companies.map((company) => (
            <CompanyCard
              key={company.id}
              company={company}
              onView={() => handleViewCompany(company.id)}
              onAssign={() => handleOpenAssignDialog(company)}
              onEdit={() => handleEditCompany(company)}
              onDelete={() => handleDeleteCompany(company.id)}
              onExport={() => handleExportCompany(company.id, company.name)}
              onImport={() => handleOpenImportCompany(company)}
            />
          ))}
        </div>
      ) : (
        <Card className="py-12">
          <CardContent className="text-center">
            <Building2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-semibold mb-2">Keine Firmen vorhanden</h3>
            <p className="text-muted-foreground mb-4">Legen Sie Ihre erste Firma an</p>
            <Button onClick={() => { resetCompanyForm(); setDialogOpen(true); }}>
              <Plus className="h-4 w-4 mr-2" />
              Neue Firma
            </Button>
          </CardContent>
        </Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">
              {editingCompany ? "Firma bearbeiten" : "Neue Firma anlegen"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleCompanySubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Firmenname *</Label>
              <Input
                value={companyFormData.name}
                onChange={(e) => setCompanyFormData({ ...companyFormData, name: e.target.value })}
                placeholder="z.B. Solar Süd GmbH"
                required
                data-testid="company-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Adresse</Label>
              <Input
                value={companyFormData.address}
                onChange={(e) => setCompanyFormData({ ...companyFormData, address: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Telefon</Label>
                <Input
                  value={companyFormData.phone}
                  onChange={(e) => setCompanyFormData({ ...companyFormData, phone: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>E-Mail</Label>
                <Input
                  type="email"
                  value={companyFormData.email}
                  onChange={(e) => setCompanyFormData({ ...companyFormData, email: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>USt-ID</Label>
                <Input
                  value={companyFormData.tax_id}
                  onChange={(e) => setCompanyFormData({ ...companyFormData, tax_id: e.target.value })}
                  placeholder="DE123456789"
                />
                <p className="text-xs text-muted-foreground">Umsatzsteuer-Identifikationsnummer</p>
              </div>
              <div className="space-y-2">
                <Label>Steuernummer</Label>
                <Input
                  value={companyFormData.tax_number}
                  onChange={(e) => setCompanyFormData({ ...companyFormData, tax_number: e.target.value })}
                  placeholder="123/456/78901"
                />
                <p className="text-xs text-muted-foreground">Steuernummer vom Finanzamt</p>
              </div>
            </div>
            
            <p className="text-sm text-amber-600 bg-amber-50 p-2 rounded">
              Mindestens USt-ID oder Steuernummer wird für die Rechnungsfinalisierung benötigt.
            </p>

            <Separator className="my-4" />

            <div className="space-y-4">
              <h4 className="font-semibold text-sm flex items-center gap-2">
                <FileJson className="h-4 w-4" />
                Nummernkreise (Startpunkt)
              </h4>
              <p className="text-xs text-muted-foreground">
                Definieren Sie hier, ab welcher Nummer die Zählung für diese Firma beginnen soll.
              </p>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Angebote ab Nr.</Label>
                  <Input
                    type="number"
                    min="1"
                    value={companyFormData.offer_number_start}
                    onChange={(e) => setCompanyFormData({ ...companyFormData, offer_number_start: parseInt(e.target.value) || 1 })}
                    placeholder="1"
                  />
                  <p className="text-xs text-muted-foreground">A-2026-XXXX</p>
                </div>
                <div className="space-y-2">
                  <Label>Rechnungen ab Nr.</Label>
                  <Input
                    type="number"
                    min="1"
                    value={companyFormData.invoice_number_start}
                    onChange={(e) => setCompanyFormData({ ...companyFormData, invoice_number_start: parseInt(e.target.value) || 1 })}
                    placeholder="1"
                  />
                  <p className="text-xs text-muted-foreground">RE-2026-XXXX</p>
                </div>
                <div className="space-y-2">
                  <Label>Gutschriften ab Nr.</Label>
                  <Input
                    type="number"
                    min="1"
                    value={companyFormData.credit_note_number_start}
                    onChange={(e) => setCompanyFormData({ ...companyFormData, credit_note_number_start: parseInt(e.target.value) || 1 })}
                    placeholder="1"
                  />
                  <p className="text-xs text-muted-foreground">GS-2026-XXXX</p>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
              <Button type="submit" data-testid="save-company-btn">
                {editingCompany ? "Speichern" : "Firma erstellen"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <CompanyDetailDialog
        company={selectedCompany}
        open={detailDialogOpen}
        onOpenChange={setDetailDialogOpen}
        onRemoveUser={handleRemoveUserFromCompany}
        onAssign={() => handleOpenAssignDialog(selectedCompany)}
        onCreateUser={() => setCreateUserDialogOpen(true)}
      />

      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Benutzer zuweisen</DialogTitle>
            <DialogDescription>
              Weisen Sie einen bestehenden Benutzer als Admin für "{companyName}" zu.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Benutzer auswählen</Label>
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger data-testid="select-user-assign">
                  <SelectValue placeholder="Benutzer wählen..." />
                </SelectTrigger>
                <SelectContent>
                  {availableUsersForAssign.length > 0 ? (
                    availableUsersForAssign.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.first_name} {user.last_name} ({user.email})
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="_none" disabled>Keine verfügbaren Benutzer</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={handleAssignUser} disabled={!selectedUserId} data-testid="confirm-assign-btn">
              <Check className="h-4 w-4 mr-2" />
              Als Admin zuweisen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={createUserDialogOpen} onOpenChange={setCreateUserDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Neuen Benutzer erstellen</DialogTitle>
            <DialogDescription>
              Erstellen Sie einen neuen Benutzer für "{companyName}".
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateUserForCompany} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Vorname *</Label>
                <Input
                  value={userFormData.first_name}
                  onChange={(e) => setUserFormData({ ...userFormData, first_name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Nachname *</Label>
                <Input
                  value={userFormData.last_name}
                  onChange={(e) => setUserFormData({ ...userFormData, last_name: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>E-Mail *</Label>
              <Input
                type="email"
                value={userFormData.email}
                onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Passwort *</Label>
              <Input
                type="password"
                value={userFormData.password}
                onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Rolle</Label>
              <Select
                value={userFormData.role}
                onValueChange={(value) => setUserFormData({ ...userFormData, role: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="vertriebler">Vertriebler</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateUserDialogOpen(false)}>Abbrechen</Button>
              <Button type="submit">
                <UserPlus className="h-4 w-4 mr-2" />
                Benutzer erstellen
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import Confirmation Dialog */}
      <AlertDialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              {importType === 'full' ? 'Komplette Datenbank importieren' : 'Firmendaten importieren'}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800">
                <p className="font-semibold mb-2">Achtung: Dieser Vorgang überschreibt vorhandene Daten!</p>
                <p className="text-sm">
                  {importType === 'full' 
                    ? 'Alle Daten in der Datenbank werden durch das Backup ersetzt.'
                    : `Alle Daten der Firma "${importCompany?.name}" werden durch das Backup ersetzt.`
                  }
                </p>
              </div>
              
              {importValidation && (
                <div className="bg-muted rounded-lg p-4">
                  <p className="font-semibold mb-2">Backup-Details:</p>
                  <div className="text-sm space-y-1">
                    <p>Typ: {importValidation.export_type === 'full_database' ? 'Komplettes Backup' : 'Firmen-Backup'}</p>
                    <p>Datum: {importValidation.export_date ? new Date(importValidation.export_date).toLocaleString('de-DE') : 'Unbekannt'}</p>
                    {importValidation.company_name && (
                      <p>Firma: {importValidation.company_name}</p>
                    )}
                    <Separator className="my-2" />
                    <p className="font-semibold">Inhalt:</p>
                    <div className="grid grid-cols-2 gap-1 mt-1">
                      {Object.entries(importValidation.summary || {}).map(([key, value]) => (
                        <p key={key} className="text-xs">
                          {key}: {typeof value === 'number' ? value : '-'}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={importing}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleImportConfirm}
              disabled={importing}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {importing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Importiere...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Ja, importieren
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
